import { useMemo } from "react"
import { max } from "date-fns"
import { api } from "~/utils/api"
import {
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  getProjectionPeriodStarts,
  getStartOfCurrentMonthUTC,
  getStartOfCurrentWeekUTC,
  getStartOfFullMonthDatesBetween,
  getStartOfWeekDatesBetween,
  startOfDayUTC,
  startOfTodayUTC,
} from "./datetime"
import { applyMetricToClosingStock, type InventoryMetric } from "~/utils/graphUtils"
import { sortBy } from "lodash"
import type { ClosingStockBreakdown } from "~/components/product/VariantTableCard/types"

const addMissingDatesClosingStock = (
  data: { date: Date; qty: number }[],
  startDate: Date,
  endDate: Date,
  monthly: boolean,
) => {
  const coveredPeriods = new Set(
    data.map((x) =>
      (monthly ? convertToStartOfMonthUTC(x.date) : convertToStartOfWeekUTC(x.date)).getTime(),
    ),
  )
  const grid = monthly
    ? getStartOfFullMonthDatesBetween(startDate, endDate)
    : getStartOfWeekDatesBetween(startDate, endDate)
  const missingDates = grid.filter((date) => !coveredPeriods.has(date.getTime()))

  return sortBy(
    data.concat(
      missingDates.map((date) => ({
        date,
        qty: 0,
      })),
    ),
    "date",
  )
}

type ClosingStockParams = {
  variantIds: string[]
  warehouseId: string | undefined
  /** Calculation (forward) window — lock it at mount (see `useFrozenForwardWindow`) to keep calculated metrics independent of a date picker. */
  startDate: Date
  endDate: Date
  monthly: boolean
  isContinuous?: boolean
  includeFashionOrders?: boolean
  purchaseIntervalDaysOverride?: number
  respectMoq?: boolean
  minMoqThresholdPct?: number
  noSafetyStockWarehouseIds?: string[]
  orderPlacementDate?: Date
  ignoreLostSales?: boolean
  /**
   * Pass when the caller wants `closingStock` / `closingStockWithOrders` returned in
   * monetary value (qty * costPrice per variant, summed). `variants` supplies the
   * costPrice lookup; null costPrice is treated as 0 to match the table.
   */
  costMode?: {
    variants: Array<{ variantId: string; costPrice: number | null }>
  }
  /**
   * Lower bound of the historic query; pass a picker's lower bound so users can scroll
   * back without touching the `[startDate, endDate]` calculation window.
   */
  historicStart?: Date
}

/**
 * Closing stock split into two queries with different lifecycles:
 *  - Future part: keyed by the `[startDate, endDate]` calculation window.
 *  - Historic part: keyed by `historicStart`; extends backward as the user scrolls a
 *    picker past a previously requested floor. Defaults to `startDate`, which keeps the
 *    historic window identical to the calculation window for plan-scoped callers.
 */
export const useGetClosingStockForDaysV2 = ({
  variantIds,
  warehouseId,
  startDate,
  endDate,
  monthly,
  isContinuous = true,
  includeFashionOrders = false,
  purchaseIntervalDaysOverride,
  respectMoq = false,
  minMoqThresholdPct = 0,
  noSafetyStockWarehouseIds,
  orderPlacementDate,
  ignoreLostSales = false,
  costMode,
  historicStart = startDate,
}: ClosingStockParams) => {
  const isCostMode = costMode !== undefined
  const metric: InventoryMetric = isCostMode ? "Cost" : "Units"

  // The current period is projected like future ones; only past periods read snapshots.
  const futureDates = useMemo(
    () => getProjectionPeriodStarts(startDate, endDate, monthly),
    [startDate, endDate, monthly],
  )

  const { data: futureClosingStocks, isLoading: isLoadingFuture } =
    api.stockOutTimes.getClosingStockAndSuggestedOrders.useQuery(
      {
        variantIds,
        warehouseId,
        dates: futureDates,
        startDate,
        endDate,
        isContinuous,
        includeFashionOrders,
        purchaseIntervalDaysOverride,
        respectMoq,
        minMoqThresholdPct,
        noSafetyStockWarehouseIds,
        orderPlacementDate,
        ignoreLostSales,
        includePerVariant: isCostMode,
      },
      { enabled: variantIds.length > 0 },
    )

  const historicDates = useMemo(() => {
    // Snapshots stop before the current period — that one is projected by the query above.
    // `max` guards against an inverted interval when `historicStart` lies in the future.
    const currentPeriodStart = monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
    const upperBound = max([historicStart, currentPeriodStart])
    const grid = monthly
      ? getStartOfFullMonthDatesBetween(historicStart, upperBound)
      : getStartOfWeekDatesBetween(historicStart, upperBound)
    return grid.filter((date) => date < currentPeriodStart)
  }, [historicStart, monthly])

  const { data: historicResult, isLoading: isLoadingHistoric } =
    api.product.historicInventoryLevelsOnSkus.useQuery(
      {
        variantIds,
        warehouseId,
        dates: historicDates,
        returnSkuLevel: false,
      },
      { enabled: variantIds.length > 0 && historicDates.length > 0 },
    )

  // Cost mode needs per-variant historic data; reuse the existing per-variant endpoint.
  // Capped at today and keyed by `historicStart` so picker moves within the already
  // fetched range don't invalidate it; future per-variant data comes from the
  // future query above.
  const { data: historicPerVariant, isLoading: isLoadingHistoricPerVariant } =
    api.product.historicInventoryLevels.useQuery(
      {
        startDate: historicStart,
        endDate: startOfTodayUTC(),
        interval: monthly ? "monthly" : "weekly",
        warehouseId,
      },
      { enabled: isCostMode && variantIds.length > 0 },
    )

  return useMemo(() => {
    const actual = historicResult?.aggregated ?? []
    const future = futureClosingStocks?.closingStock ?? []
    const futureSuggested = futureClosingStocks?.closingStockWithOrders ?? []

    let closingStockRaw: { date: Date; qty: number }[]
    let closingStockWithOrdersRaw: { date: Date; qty: number }[]

    if (isCostMode && costMode) {
      // Build per-variant flat arrays from both sources, run them through the helper.
      const perVariantHistoric: Array<{ variantId: string; date: Date; quantity: number }> = []
      if (historicPerVariant?.data && historicPerVariant.dates) {
        const histDates = historicPerVariant.dates as unknown as Date[]
        for (const [variantId, levels] of historicPerVariant.data.entries()) {
          levels.forEach((qty, i) => {
            const d = histDates[i]
            if (d) perVariantHistoric.push({ variantId, date: d, quantity: qty })
          })
        }
      }

      const buildPerVariantFuture = (
        levels: Record<string, { date: Date; qty: number }[]> | undefined,
      ) => {
        const out: Array<{ variantId: string; date: Date; quantity: number }> = []
        if (!levels) return out
        for (const [variantId, arr] of Object.entries(levels)) {
          for (const { date, qty } of arr) {
            out.push({ variantId, date, quantity: qty })
          }
        }
        return out
      }

      const perVariantFuture = buildPerVariantFuture(futureClosingStocks?.perVariantClosingStock)
      const perVariantFutureWithOrders = buildPerVariantFuture(
        futureClosingStocks?.perVariantClosingStockWithOrders,
      )

      const closingCost = applyMetricToClosingStock(
        [...perVariantHistoric, ...perVariantFuture],
        costMode.variants,
        metric,
      )
      const closingWithOrdersCost = applyMetricToClosingStock(
        [...perVariantHistoric, ...perVariantFutureWithOrders],
        costMode.variants,
        metric,
      )

      closingStockRaw = closingCost.map((x) => ({ date: x.date, qty: Math.round(x.quantity) }))
      closingStockWithOrdersRaw = closingWithOrdersCost.map((x) => ({
        date: x.date,
        qty: Math.round(x.quantity),
      }))
    } else {
      closingStockRaw = [...actual, ...future]
      closingStockWithOrdersRaw = [...actual, ...futureSuggested]
    }

    // Earliest historic inventory period. `actual` only contains periods from the first
    // known StockRecord onward (periods before are dropped server-side), so its min date is
    // the variant's first known inventory date. Everything the gap-filler adds before it is
    // "no data" and should render as a dash, not 0. `null` when we have no records at all.
    const firstActualDate = actual.reduce<Date | null>(
      (min, r) => (min === null || r.date < min ? r.date : min),
      null,
    )
    const firstInventoryDate = firstActualDate
      ? monthly
        ? convertToStartOfMonthUTC(firstActualDate)
        : firstActualDate
      : null

    const closingStock = addMissingDatesClosingStock(
      closingStockRaw,
      historicStart,
      endDate,
      monthly,
    )
    const closingStockWithOrders = addMissingDatesClosingStock(
      closingStockWithOrdersRaw,
      historicStart,
      endDate,
      monthly,
    )

    const normalize = (item: { date: Date; qty: number }) => ({
      date: monthly ? convertToStartOfMonthUTC(item.date) : item.date,
      quantity: Math.max(item.qty, 0),
    })

    const closingStockBreakdownByDate = new Map<number, ClosingStockBreakdown>(
      (futureClosingStocks?.closingStockBreakdown ?? []).map((b) => [
        (monthly ? convertToStartOfMonthUTC(b.date) : b.date).getTime(),
        b,
      ]),
    )

    return {
      closingStock: closingStock.map(normalize),
      closingStockWithOrders: closingStockWithOrders.map(normalize),
      closingStockBreakdownByDate,
      orders: futureClosingStocks?.suggestedOrders ?? [],
      firstInventoryDate,
      isLoading:
        isLoadingFuture || isLoadingHistoric || (isCostMode && isLoadingHistoricPerVariant),
    }
  }, [
    historicResult,
    futureClosingStocks,
    historicPerVariant,
    historicStart,
    endDate,
    monthly,
    isLoadingFuture,
    isLoadingHistoric,
    isLoadingHistoricPerVariant,
    isCostMode,
    costMode,
    metric,
  ])
}

/**
 * Suggested orders for the NOOS supply plan flows (buy dialog, reorder dialog, suggestions card).
 *
 * - `orderDate`: when this order is placed. Maps to the backend's `orderPlacementDate`, so the
 *   algorithm derives arrival as `orderDate + leadTime per variant` (proper lead-time handling).
 *   If omitted, the algorithm falls back to `today + leadTime` — "place now" semantics.
 * - `nextOrderDate`: when the next order after this one is placed. Used as the coverage
 *   horizon — variants stocking out before then are flagged.
 * - `purchaseIntervalDays`: explicit coverage window length, independent of the date pair.
 *   `nextOrderDate - orderDate` for exceptional reorders; the supply plan's purchaseInterval
 *   for the regular buy flow.
 *
 * Inputs are floored to UTC midnight so that pre-save dates (carrying a "now" time component)
 * and post-save dates (re-hydrated from a Postgres `@db.Date` column as UTC midnight) hit the
 * same React Query cache key and produce identical suggestions. Without this, the Buy created
 * via `CreateBuyDialog` and the Suggested order column on `BuyTable` disagreed by 1–3 units
 * per variant for the same buyDate (SLIM-350).
 *
 * No historic-inventory fetch — the suggestion tables don't display closing stock.
 */
type NoosSuggestedOrdersArgs = {
  variantIds: string[]
  orderDate?: Date
  nextOrderDate: Date
  purchaseIntervalDays: number
  warehouseId?: string
  includeFashionOrders?: boolean
  respectMoq?: boolean
  // Percentage 0-N. Only honored when respectMoq is true. If a group's pre-scale demand
  // is < (pct/100) × secondaryMoq, the group's suggestions are dropped to 0 instead of
  // being rounded up to MOQ.
  minMoqThresholdPct?: number
  noSafetyStockWarehouseIds?: string[]
  includeDraftOrdersAsIntent?: boolean
  excludePurchasePlanId?: number
}

/** Query input behind `useSuggestedOrdersForNoosSupply`, for fetching the same data imperatively. */
export function buildNoosSuggestedOrdersInput({
  variantIds,
  orderDate,
  nextOrderDate,
  purchaseIntervalDays,
  warehouseId,
  includeFashionOrders = false,
  respectMoq = false,
  minMoqThresholdPct = 0,
  noSafetyStockWarehouseIds,
  includeDraftOrdersAsIntent = false,
  excludePurchasePlanId,
}: NoosSuggestedOrdersArgs) {
  const normalizedOrderDate = orderDate ? startOfDayUTC(orderDate) : undefined
  const normalizedNextOrderDate = startOfDayUTC(nextOrderDate)
  return {
    variantIds,
    warehouseId,
    dates: [normalizedOrderDate ?? startOfTodayUTC()],
    endDate: normalizedNextOrderDate,
    isContinuous: false,
    includeFashionOrders,
    purchaseIntervalDaysOverride: purchaseIntervalDays,
    orderPlacementDate: normalizedOrderDate,
    respectMoq,
    minMoqThresholdPct,
    noSafetyStockWarehouseIds,
    includeDraftOrdersAsIntent,
    excludePurchasePlanId,
  }
}

export const useSuggestedOrdersForNoosSupply = ({
  enabled = true,
  ...args
}: NoosSuggestedOrdersArgs & { enabled?: boolean }) => {
  return api.stockOutTimes.getClosingStockAndSuggestedOrders.useQuery(
    buildNoosSuggestedOrdersInput(args),
    { enabled: enabled && args.variantIds.length > 0 },
  )
}
